import axios from 'axios';

class TOSUploader {
  /**
   * 上传视频到 TOS
   * @param {File} videoFile - 视频文件
   * @param {string} accessToken - 访问令牌
   * @param {Function} onProgress - 进度回调 (0-100)
   * @returns {Promise<{objectKey: string, size: number}>}
   */
  async uploadVideo(videoFile, accessToken, onProgress) {
    try {
      console.log('开始上传视频到 TOS:', videoFile.name);

      // 1. 获取预签名 URL
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001/api';

      const presignedResponse = await axios.post(
        `${API_BASE_URL}/tos/presigned-url`,
        {
          filename: videoFile.name,
          fileSize: videoFile.size,
          contentType: videoFile.type
        },
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        }
      );

      if (!presignedResponse.data.success) {
        throw new Error('获取上传地址失败');
      }

      const { uploadUrl, objectKey } = presignedResponse.data.data;
      console.log('获取预签名 URL 成功');

      // 2. 直接上传到 TOS
      console.log('开始上传到 TOS...');

      await axios.put(uploadUrl, videoFile, {
        headers: {
          'Content-Type': videoFile.type || 'video/mp4'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.floor((progressEvent.loaded / progressEvent.total) * 100);
          if (onProgress) {
            onProgress(progress);
          }
          console.log(`上传进度: ${progress}%`);
        },
        timeout: 600000, // 10 分钟超时
      });

      console.log('✓ 视频上传完成');

      return {
        objectKey,
        size: videoFile.size
      };

    } catch (error) {
      console.error('视频上传失败:', error);

      let errorMessage = '视频上传失败';
      if (error.response?.status === 401) {
        errorMessage = '登录已过期，请重新登录';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = '上传超时，请检查网络连接';
      } else if (error.message) {
        errorMessage = error.message;
      }

      throw new Error(errorMessage);
    }
  }

  /**
   * 上传音频到 TOS
   * @param {Blob} audioBlob - 音频 Blob
   * @param {string} filename - 文件名
   * @param {string} accessToken - 访问令牌
   * @param {Function} onProgress - 进度回调 (0-100)
   * @returns {Promise<{objectKey: string, size: number}>}
   */
  async uploadAudio(audioBlob, filename, accessToken, onProgress) {
    try {
      console.log('开始上传音频到 TOS:', filename);

      // 1. 获取预签名 URL
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001/api';

      const presignedResponse = await axios.post(
        `${API_BASE_URL}/tos/presigned-url`,
        {
          filename: filename,
          fileSize: audioBlob.size,
          contentType: 'audio/wav'
        },
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        }
      );

      if (!presignedResponse.data.success) {
        throw new Error('获取上传地址失败');
      }

      const { uploadUrl, objectKey } = presignedResponse.data.data;
      console.log('获取预签名 URL 成功');

      // 2. 直接上传到 TOS
      console.log('开始上传到 TOS...');
      console.log('音频大小:', audioBlob.size, 'bytes');

      const uploadResponse = await axios.put(uploadUrl, audioBlob, {
        headers: {
          'Content-Type': 'audio/wav'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.floor((progressEvent.loaded / progressEvent.total) * 100);
          if (onProgress) {
            onProgress(progress);
          }
          console.log(`上传进度: ${progress}%`);
        },
        timeout: 600000, // 10 分钟超时
      });

      console.log('上传响应状态:', uploadResponse.status);

      // 验证上传是否成功
      if (uploadResponse.status !== 200 && uploadResponse.status !== 204) {
        throw new Error(`上传失败，状态码: ${uploadResponse.status}`);
      }

      console.log('✓ 音频上传完成');

      return {
        objectKey,
        size: audioBlob.size
      };

    } catch (error) {
      console.error('音频上传失败:', error);

      let errorMessage = '音频上传失败';
      if (error.response?.status === 401) {
        errorMessage = '登录已过期，请重新登录';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = '上传超时，请检查网络连接';
      } else if (error.message) {
        errorMessage = error.message;
      }

      throw new Error(errorMessage);
    }
  }

  /**
   * 获取视频时长
   * @param {File} videoFile - 视频文件
   * @returns {Promise<number>} 时长（秒）
   */
  async getVideoDuration(videoFile) {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };

      video.onerror = () => {
        reject(new Error('无法获取视频时长'));
      };

      video.src = URL.createObjectURL(videoFile);
    });
  }
}

const tosUploader = new TOSUploader();
export default tosUploader;
